import { useCallback, useEffect } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { pick } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import AccessControl from '@gef-ui/cdu-user/components/AccessControl';
import { hasPermissionByIds } from '@gef-ui/cdu-user/selectors';
import useValidateFormPromise from '@gef-ui/form-core/hooks/form/useValidateFormPromise';
import useControlNext from '@gef-ui/form-core/hooks/state/useControlNext';
import { setValue } from '@gef-ui/form-core/stream/transfer/control';
import Autocomplete from '@gef-ui/form-material/Autocomplete';
import Checkbox from '@gef-ui/form-material/Checkbox';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';
import MagnifyingGlass from '@gef-ui/material-icons/MagnifyingGlass';

import AutocompleteCustomOptionKey from 'components/atoms/GefMui/AutocompleteCustomOptionKey';
import DatePickerFullWidth from 'components/atoms/GefMui/DatePickerFullWidth';
import { codelists } from 'constants/codelists';
import { MaxLengths } from 'constants/forms';
import MODALS from 'constants/modals';
import {
	KCL_APPLY_POLICY_SUBSELLER_FILTER,
	KCL_DISPLAY_POLICY_LIST_SUBORD,
	KCL_DISPLAY_SEMAPHORE,
} from 'features/common/acl/constants';
import { urls } from 'features/common/fetch/constants';
import { getIsLoaded } from 'features/common/fetch/selectors';
import policyOverview from 'features/views/policyOverview';
import { resetCriteria, saveFocusField, search } from 'features/views/policyOverview/actions';
import { FIELDS, POLICY_OVERVIEW_FILTER_FORM_NAME } from 'features/views/policyOverview/form';
import { getFocusField } from 'features/views/policyOverview/selectors';
import user from 'features/views/user';
import { getHasSubordinates, getUser } from 'features/views/user/selectors';
import userContext from 'features/views/userContext';
import { hasContextPermissionById } from 'features/views/userContext/selectors';
import { getCodelistByName } from 'utils/codelists';
import SemaphoreAutoComplete from 'pages/Semaphore/components/SemaphoreAutoComplete';

import ProductAutoComplete from '../ProductAutoComplete';
import SubordinateLoginSearchModal from './SubordinateLoginSearchModal';

const AdvancedSearchModalBody = () => {
	const dispatch = useDispatch();
	const next = useControlNext(POLICY_OVERVIEW_FILTER_FORM_NAME);
	const focusField = useSelector(getFocusField);

	const subsellerLoginSearchModal = useModal(MODALS.policyOverview_subordinate_login_search);
	const advancedSearchModal = useModal(MODALS.policyOverview_advanced_search);
	const hasPermissionToApplySubsellerFilter = useSelector((state) =>
		hasPermissionByIds(state)(KCL_APPLY_POLICY_SUBSELLER_FILTER),
	);
	const userLogon = useSelector(getUser);
	const contextUser = useSelector(userContext.selectors.getContextUser);
	const hasPPZSubordinatesOrIsPPZSubordinate = useSelector(user.selectors.getHasPPZSubordinatesOrIsPPZSubordinate);
	const isPPZSubordinate = useSelector(user.selectors.getIsPPZSubordinate);
	const areSellerNumbersLoaded = useSelector(getIsLoaded(urls.userSellerIns));
	const isSellerLoginFieldActive = !isPPZSubordinate || hasPermissionToApplySubsellerFilter;

	const userHasSubordinates = useSelector(getHasSubordinates);
	const hasSubordinates = contextUser ? contextUser?.hasSubordinates : userHasSubordinates;

	const userDisplaySubOrdPolicyListPerm = useSelector((state) =>
		hasPermissionByIds(state)(KCL_DISPLAY_POLICY_LIST_SUBORD),
	);
	const contextDisplaySubOrdPolicyListPerm = useSelector((state) =>
		hasContextPermissionById(state, KCL_DISPLAY_POLICY_LIST_SUBORD),
	);
	const hasDisplaySubOrdPolicyList = contextUser
		? contextDisplaySubOrdPolicyListPerm
		: userDisplaySubOrdPolicyListPerm;

	const showWithSubordinatesCheckbox = hasDisplaySubOrdPolicyList && hasSubordinates;

	useEffect(() => {
		if (focusField) {
			const elements = document.querySelectorAll(`#policyOverview-filter-${focusField}`);
			// applied workaround instead of manualFocus, because some fields have multiple instances (mainFilter + advancedSearch)
			if (elements.length > 1) {
				elements[1].focus();
			} else {
				elements[0].focus();
			}
		}

		if (!areSellerNumbersLoaded) {
			if (contextUser) {
				dispatch(policyOverview.actions.getUsersSellerNumbers(contextUser.login));
			} else {
				dispatch(policyOverview.actions.getUsersSellerNumbers(userLogon));
			}
		}
	}, [dispatch, contextUser, userLogon, areSellerNumbersLoaded, focusField]);

	useEffect(
		() => () => {
			dispatch(saveFocusField(null));
		},
		[dispatch],
	);

	const sellerIns = useSelector(policyOverview.selectors.getSellerNumbers);

	// codelist should be already fetched by "Filter" parent component
	const policyStatusesCodelist = useSelector(getCodelistByName(codelists.policyStatuses));
	const productDomainsCodelist = useSelector(getCodelistByName(codelists.productDomains));
	const policyPaymentStatusesCodelist = useSelector(getCodelistByName(codelists.policyPaymentStatuses));

	const handleOpenSubModal = useCallback(() => {
		NiceModal.show(MODALS.policyOverview_subordinate_login_search);
	}, []);

	const validateForm = useValidateFormPromise(POLICY_OVERVIEW_FILTER_FORM_NAME);
	const handleSubmit = useCallback(async () => {
		try {
			await validateForm();
		} catch (error) {
			const { policyNumber: psNumberValidations } = pick(['policyNumber'], error);
			if (psNumberValidations.length === 1 && psNumberValidations[0]?.type === 'WARNING') {
				dispatch(search());
				advancedSearchModal.hide();
			}
			return;
		}
		dispatch(search());
		advancedSearchModal.hide();
	}, [dispatch, advancedSearchModal, validateForm]);

	const handleReset = useCallback(() => {
		dispatch(resetCriteria());
	}, [dispatch]);

	const resetProductAutoComplete = useCallback(() => {
		next(setValue({ fieldName: FIELDS.product, value: null }));
	}, [next]);

	const handleSelectSubsellerLogin = useCallback(
		(subsellerLogin) => () => {
			subsellerLoginSearchModal.hide();
			next(setValue({ fieldName: FIELDS.sellerLogin, value: subsellerLogin }));
		},
		[subsellerLoginSearchModal, next],
	);

	return (
		<Stack spacing={3}>
			<SubordinateLoginSearchModal
				id={MODALS.policyOverview_subordinate_login_search}
				onSelectSubsellerLogin={handleSelectSubsellerLogin}
			/>
			<Grid container>
				<Grid size={12}>
					<Grid container spacing={1}>
						<Grid size={12}>
							<Typography variant="h5" color="text.primary">
								{getLabelMessage('policyOverview:clientInfo')}
							</Typography>
						</Grid>
						<Grid size={2}>
							<TextField
								size="small"
								fieldName={FIELDS.firstName}
								label={getLabelMessage('policyOverview:firstName')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.NAME,
									},
								}}
							/>
						</Grid>
						<Grid size={2}>
							<TextField
								size="small"
								fieldName={FIELDS.surname}
								label={getLabelMessage('policyOverview:surname')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.SURNAME,
									},
								}}
							/>
						</Grid>
						<Grid size={3}>
							<TextField
								size="small"
								fieldName={FIELDS.businessName}
								label={getLabelMessage('policyOverview:businessName')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.BUSINESS_NAME,
									},
								}}
							/>
						</Grid>
						<Tooltip title={getLabelMessage('tooltips:taxNumber')}>
							<Grid size={2}>
								<TextField
									size="small"
									fieldName={FIELDS.businessIn}
									label={getLabelMessage('policyOverview:businessIn')}
									slotProps={{
										htmlInput: {
											maxLength: MaxLengths.TAX_NUMBER,
										},
									}}
								/>
							</Grid>
						</Tooltip>
						<Grid size={2}>
							<TextField
								size="small"
								fieldName={FIELDS.municipality}
								label={getLabelMessage('policyOverview:municipality')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.MUNICIPALITY,
									},
								}}
							/>
						</Grid>
						<Tooltip title={getLabelMessage('tooltips:postalCode')}>
							<Grid size={1}>
								<TextField
									size="small"
									fieldName={FIELDS.zipCode}
									label={getLabelMessage('policyOverview:zipCode')}
									slotProps={{
										htmlInput: {
											maxLength: MaxLengths.ZIP_CODE,
										},
									}}
								/>
							</Grid>
						</Tooltip>
						<Grid size={2}>
							<AccessControl accessLevel={KCL_DISPLAY_SEMAPHORE}>
								<SemaphoreAutoComplete fieldName={FIELDS.semaphore} />
							</AccessControl>
						</Grid>
						<Grid size={3}>
							<TextField
								size="small"
								fieldName={FIELDS.policyHolderAnnualBirthDateFrom}
								label={getLabelMessage('policyOverview:policyHolderAnnualBirthDateFrom')}
								placeholder={getLabelMessage('dateFormats:ddmm')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.DAY_MONTH_DATE,
									},
								}}
							/>
						</Grid>
						<Grid size={3}>
							<TextField
								size="small"
								fieldName={FIELDS.policyHolderAnnualBirthDateTo}
								label={getLabelMessage('policyOverview:policyHolderAnnualBirthDateTo')}
								placeholder={getLabelMessage('dateFormats:ddmm')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.DAY_MONTH_DATE,
									},
								}}
							/>
						</Grid>
						<Grid size={2}>
							<TextField
								size="small"
								fieldName={FIELDS.birthYearFrom}
								label={getLabelMessage('policyOverview:birthYearFrom')}
								placeholder={getLabelMessage('dateFormats:yyyy')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.YEAR_DATE,
									},
								}}
							/>
						</Grid>
						<Grid size={2}>
							<TextField
								size="small"
								fieldName={FIELDS.birthYearTo}
								label={getLabelMessage('policyOverview:birthYearTo')}
								placeholder={getLabelMessage('dateFormats:yyyy')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.YEAR_DATE,
									},
								}}
							/>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
			<Grid container>
				<Grid size={12}>
					<Grid container spacing={1}>
						<Grid size={12}>
							<Typography variant="h5" color="text.primary">
								{getLabelMessage('policyOverview:policyInfo')}
							</Typography>
						</Grid>
						<Tooltip title={getLabelMessage('tooltips:policyNo')}>
							<Grid size={3}>
								<TextField
									size="small"
									fieldName={FIELDS.policyNumber}
									label={getLabelMessage('policyOverview:policyNumber')}
									slotProps={{
										htmlInput: {
											maxLength: MaxLengths.POLICY_NO,
										},
									}}
								/>
							</Grid>
						</Tooltip>
						<Tooltip title={getLabelMessage('tooltips:policyFrameworkNo')}>
							<Grid size={3}>
								<TextField
									size="small"
									fieldName={FIELDS.rsNumber}
									label={getLabelMessage('policyOverview:rsNumber')}
									slotProps={{
										htmlInput: {
											maxLength: MaxLengths.POLICY_FRAMEWORK_NO,
										},
									}}
								/>
							</Grid>
						</Tooltip>
						<Grid size={3}>
							<AutocompleteCustomOptionKey
								size="small"
								fieldName={FIELDS.policyStatus}
								label={getLabelMessage('policyOverview:policyStatus')}
								options={policyStatusesCodelist}
								labelPropName="description"
								keyPropName="id"
							/>
						</Grid>
						<Grid size={3}>
							<Autocomplete
								size="small"
								fieldName={FIELDS.policyPaymentStatus}
								label={getLabelMessage('policyOverview:policyPaymentStatus')}
								options={policyPaymentStatusesCodelist}
								labelPropName="shortDescription"
							/>
						</Grid>
						<Grid size={4}>
							<Autocomplete
								size="small"
								fieldName={FIELDS.productDomain}
								label={getLabelMessage('policyOverview:productDomain')}
								options={productDomainsCodelist}
								labelPropName="description"
								onChange={resetProductAutoComplete}
							/>
						</Grid>
						<Grid size={4}>
							<ProductAutoComplete />
						</Grid>
						<Tooltip title={getLabelMessage('tooltips:carRegNo')}>
							<Grid size={4}>
								<TextField
									size="small"
									fieldName={FIELDS.carRegNo}
									label={getLabelMessage('policyOverview:carRegNo')}
									slotProps={{
										htmlInput: {
											maxLength: MaxLengths.CAR_REG_NO,
										},
									}}
								/>
							</Grid>
						</Tooltip>
						<Grid size={3}>
							<DatePickerFullWidth
								fieldName={FIELDS.policyValueDateFrom}
								label={getLabelMessage('policyOverview:policyValueDateFrom')}
							/>
						</Grid>
						<Grid size={3}>
							<DatePickerFullWidth
								fieldName={FIELDS.policyValueDateTo}
								label={getLabelMessage('policyOverview:policyValueDateTo')}
							/>
						</Grid>
						<Grid size={3}>
							<DatePickerFullWidth
								fieldName={FIELDS.policyOpenDateFrom}
								label={getLabelMessage('policyOverview:policyOpenDateFrom')}
							/>
						</Grid>
						<Grid size={3}>
							<DatePickerFullWidth
								fieldName={FIELDS.policyOpenDateTo}
								label={getLabelMessage('policyOverview:policyOpenDateTo')}
							/>
						</Grid>
						<Grid size={3}>
							<DatePickerFullWidth
								fieldName={FIELDS.policyFinalDateFrom}
								label={getLabelMessage('policyOverview:policyFinalDateFrom')}
							/>
						</Grid>
						<Grid size={3}>
							<DatePickerFullWidth
								fieldName={FIELDS.policyFinalDateTo}
								label={getLabelMessage('policyOverview:policyFinalDateTo')}
							/>
						</Grid>
						<Grid size={6} />
						<Grid size={3}>
							<TextField
								size="small"
								fieldName={FIELDS.policyValueDateAnnualFrom}
								label={getLabelMessage('policyOverview:policyValueDateAnnualFrom')}
								placeholder={getLabelMessage('dateFormats:ddmm')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.DAY_MONTH_DATE,
									},
								}}
							/>
						</Grid>
						<Grid size={3}>
							<TextField
								size="small"
								fieldName={FIELDS.policyValueDateAnnualTo}
								label={getLabelMessage('policyOverview:policyValueDateAnnualTo')}
								placeholder={getLabelMessage('dateFormats:ddmm')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.DAY_MONTH_DATE,
									},
								}}
							/>
						</Grid>
						<Grid size={3}>
							<TextField
								size="small"
								fieldName={FIELDS.policyOpenDateAnnualFrom}
								label={getLabelMessage('policyOverview:policyOpenDateAnnualFrom')}
								placeholder={getLabelMessage('dateFormats:ddmm')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.DAY_MONTH_DATE,
									},
								}}
							/>
						</Grid>
						<Grid size={3}>
							<TextField
								size="small"
								fieldName={FIELDS.policyOpenDateAnnualTo}
								label={getLabelMessage('policyOverview:policyOpenDateAnnualTo')}
								placeholder={getLabelMessage('dateFormats:ddmm')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.DAY_MONTH_DATE,
									},
								}}
							/>
						</Grid>
						<Grid size={12}>
							<Grid container spacing={2}>
								<Grid size={1.5}>
									<Checkbox
										defaultValue
										fieldName={FIELDS.gcpContractsOnly}
										label={getLabelMessage('policyOverview:gcpContractsOnly')}
									/>
								</Grid>
								<Grid size={1.5}>
									<Checkbox
										defaultValue
										fieldName={FIELDS.gpContractsOnly}
										label={getLabelMessage('policyOverview:gpContractsOnly')}
									/>
								</Grid>
								<Grid size={2}>
									<Checkbox
										fieldName={FIELDS.frameworkPolicyOnly}
										label={getLabelMessage('policyOverview:frameworkPolicyOnly')}
									/>
								</Grid>

								<Tooltip title={getLabelMessage('tooltips:activePolicyWoQuotationOnly')}>
									<Grid size={1.75}>
										<Checkbox
											fieldName={FIELDS.activePolicyWoQuotationOnly}
											label={getLabelMessage('policyOverview:activePolicyWoQuotationOnly')}
										/>
									</Grid>
								</Tooltip>
								{showWithSubordinatesCheckbox && (
									<Grid size={2}>
										<Checkbox
											defaultValue={false}
											fieldName={FIELDS.withSubordinates}
											label={getLabelMessage('policyOverview:withSubordinates')}
										/>
									</Grid>
								)}
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
			<Grid container>
				<Grid size={12}>
					<Grid container spacing={1}>
						<Grid size={12}>
							<Typography variant="h5" color="text.primary">
								{getLabelMessage('policyOverview:policyHolderInfo')}
							</Typography>
						</Grid>
						<Grid size={3}>
							<Autocomplete
								size="small"
								fieldName={FIELDS.policyHolderNumber}
								options={sellerIns.map((sellerIn) => ({
									description: sellerIn.toString(),
									id: sellerIn,
								}))}
								labelPropName="description"
								label={getLabelMessage('policyOverview:policyHolderNumber')}
							/>
						</Grid>
						{hasPPZSubordinatesOrIsPPZSubordinate && (
							<Grid size={3}>
								<TextField
									size="small"
									fieldName={FIELDS.sellerLogin}
									label={getLabelMessage('policyOverview:sellerLogin')}
									disabled={!isSellerLoginFieldActive}
									slotProps={{
										htmlInput: {
											maxLength: MaxLengths.LOGIN,
										},

										input: {
											endAdornment: (
												<IconButton
													sx={{ cursor: 'pointer' }}
													onClick={handleOpenSubModal}
													disabled={!isSellerLoginFieldActive}
												>
													<MagnifyingGlass />
												</IconButton>
											),
										},
									}}
								/>
							</Grid>
						)}
					</Grid>
				</Grid>
			</Grid>
			<Grid container>
				<Grid size={12}>
					<Grid
						container
						spacing={2}
						sx={{
							justifyContent: 'flex-end',
						}}
					>
						<Grid size={3}>
							<Button variant="outlined" onClick={handleReset} fullWidth>
								{getLabelMessage('common:resetButton')}
							</Button>
						</Grid>
						<Grid size={3}>
							<Button variant="contained" onClick={handleSubmit} fullWidth>
								{getLabelMessage('common:searchButton')}
							</Button>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Stack>
	);
};

export default AdvancedSearchModalBody;
